import React from 'react';

// ── Inline styles ─────────────────────────────────────────────────────────────
export const S = {
  scrollArea: {
    padding: '0 32px 40px',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: 14,
    color: '#323130',
    lineHeight: 1.6,
  } as React.CSSProperties,
  tocHeader: {
    fontSize: 22,
    fontWeight: 300,
    color: '#0078d4',
    margin: '32px 0 16px',
    paddingBottom: 8,
    borderBottom: '2px solid #0078d4',
  } as React.CSSProperties,
  tocGroup: { marginBottom: 4 } as React.CSSProperties,
  tocLink: {
    display: 'block',
    fontSize: 14,
    fontWeight: 600,
    color: '#323130',
    cursor: 'pointer',
    padding: '4px 0',
    borderBottom: '1px dotted #c8c8c8',
    textDecoration: 'none',
  } as React.CSSProperties,
  tocSubLink: {
    display: 'block',
    fontSize: 13,
    color: '#0078d4',
    cursor: 'pointer',
    padding: '2px 0 2px 16px',
    textDecoration: 'none',
  } as React.CSSProperties,
  chapterHeader: {
    background: 'linear-gradient(135deg, #0078d4, #106ebe)',
    color: 'white',
    padding: '20px 24px 16px',
    margin: '32px -32px 20px',
  } as React.CSSProperties,
  chapterNum: {
    fontSize: 11,
    opacity: 0.75,
    letterSpacing: 2,
    textTransform: 'uppercase' as const,
    marginBottom: 4,
  } as React.CSSProperties,
  chapterTitle: { fontSize: 22, fontWeight: 300, margin: 0 } as React.CSSProperties,
  h3: {
    fontSize: 17,
    fontWeight: 600,
    color: '#0078d4',
    margin: '28px 0 10px',
    paddingBottom: 5,
    borderBottom: '1px solid #edebe9',
  } as React.CSSProperties,
  h4: { fontSize: 14, fontWeight: 600, color: '#323130', margin: '18px 0 8px' } as React.CSSProperties,
  infoBox: {
    background: '#eff6fc',
    borderLeft: '4px solid #0078d4',
    padding: '10px 14px',
    margin: '12px 0',
    borderRadius: '0 4px 4px 0',
    fontSize: 13,
  } as React.CSSProperties,
  warnBox: {
    background: '#fff4ce',
    borderLeft: '4px solid #d83b01',
    padding: '10px 14px',
    margin: '12px 0',
    borderRadius: '0 4px 4px 0',
    fontSize: 13,
  } as React.CSSProperties,
  tipBox: {
    background: '#dff6dd',
    borderLeft: '4px solid #107c10',
    padding: '10px 14px',
    margin: '12px 0',
    borderRadius: '0 4px 4px 0',
    fontSize: 13,
  } as React.CSSProperties,
  placeholder: {
    background: '#f3f2f1',
    border: '2px dashed #c8c8c8',
    borderRadius: 4,
    padding: '24px 16px',
    textAlign: 'center' as const,
    margin: '14px 0',
    color: '#605e5c',
    fontSize: 13,
  } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, margin: '12px 0', fontSize: 13 },
  th: {
    background: '#0078d4',
    color: 'white',
    padding: '8px 10px',
    textAlign: 'left' as const,
    fontWeight: 600,
    fontSize: 13,
  } as React.CSSProperties,
  td: { padding: '6px 10px', borderBottom: '1px solid #edebe9', verticalAlign: 'top' as const } as React.CSSProperties,
  tdEven: { padding: '6px 10px', borderBottom: '1px solid #edebe9', verticalAlign: 'top' as const, background: '#f3f2f1' } as React.CSSProperties,
  ul: { margin: '8px 0 12px', paddingLeft: 20 } as React.CSSProperties,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
export const InfoBox: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={S.infoBox}>
    {title && <strong style={{ display: 'block', marginBottom: 4 }}>{title}</strong>}
    {children}
  </div>
);
export const WarnBox: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={S.warnBox}>
    {title && <strong style={{ display: 'block', marginBottom: 4 }}>{title}</strong>}
    {children}
  </div>
);
export const TipBox: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={S.tipBox}>
    {title && <strong style={{ display: 'block', marginBottom: 4 }}>{title}</strong>}
    {children}
  </div>
);
export const Placeholder: React.FC<{ label: string; desc?: string; src?: string }> = ({ label, desc, src }) => {
  if (src) {
    return (
      <div style={{ margin: '14px 0', textAlign: 'center' }}>
        <img
          src={src}
          alt={label}
          style={{ maxWidth: '100%', borderRadius: 4, border: '1px solid #edebe9', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        />
        <div style={{ fontSize: 11, color: '#a19f9d', marginTop: 4 }}>{label}</div>
      </div>
    );
  }
  return (
    <div style={S.placeholder}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#0078d4', marginBottom: 2 }}>[SCREENSHOT: {label}]</div>
      {desc && <div style={{ fontSize: 11, color: '#a19f9d' }}>{desc}</div>}
    </div>
  );
};

interface TRowProps { cells: string[]; even?: boolean }
const TR: React.FC<TRowProps> = ({ cells, even }) =>
  <tr>{cells.map((c, i) => <td key={i} style={even ? S.tdEven : S.td} dangerouslySetInnerHTML={{ __html: c }} />)}</tr>;

interface TableProps { headers: string[]; rows: string[][] }
export const Table: React.FC<TableProps> = ({ headers, rows }) => (
  <table style={S.table}>
    <thead><tr>{headers.map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr></thead>
    <tbody>{rows.map((r, i) => <TR key={i} cells={r} even={i % 2 === 1} />)}</tbody>
  </table>
);

export const BackToToc: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <div style={{ textAlign: 'right', marginTop: 28, marginBottom: 4, borderTop: '1px solid #edebe9', paddingTop: 8 }}>
    <a onClick={onClick} style={{ fontSize: 12, color: '#0078d4', cursor: 'pointer', userSelect: 'none' }}>
      ↑ Zurück zum Inhaltsverzeichnis
    </a>
  </div>
);

export const Steps: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ol style={{ listStyle: 'none', padding: 0, margin: '10px 0 14px' }}>
    {items.map((item, i) => (
      <li key={i} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <span style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 26, height: 26, borderRadius: '50%',
          background: '#0078d4', color: 'white', fontSize: 12, fontWeight: 700,
          marginTop: 1, flexShrink: 0,
        }}>{i + 1}</span>
        <span style={{ paddingTop: 3 }}>{item}</span>
      </li>
    ))}
  </ol>
);

// ── Main Content Component ────────────────────────────────────────────────────
interface HandbuchContentProps {
  scrollTo: (id: string) => void;
  isPrivileged: boolean;
}

export const HandbuchContent: React.FC<HandbuchContentProps> = ({ scrollTo, isPrivileged }) => {
  const tocEntries = isPrivileged ? [
    ['k1', '1 · Einleitung und Systemüberblick', ['k1-1:1.1 Zweck und Einsatzbereiche', 'k1-2:1.2 Technische Voraussetzungen', 'k1-3:1.3 Benutzerrollen']],
    ['k2', '2 · Anmeldung und Navigation', ['k2-1:2.1 Anmeldung', 'k2-2:2.2 Hauptnavigation', 'k2-3:2.3 Benutzermenü und Hilfe']],
    ['k3', '3 · Bestellungen – Übersicht', ['k3-1:3.1 Bestellliste und Filter', 'k3-2:3.2 Neue Bestellung', 'k3-3:3.3 Bestellung übernehmen', 'k3-4:3.4 Massenaktionen']],
    ['k4', '4 · Bestelldetail', ['k4-1:4.1 Bestellkopf', 'k4-2:4.2 Positionen verwalten', 'k4-3:4.3 Position bearbeiten', 'k4-4:4.4 Globale Einstellungen', 'k4-5:4.5 Vertraulich', 'k4-6:4.6 Dokumente', 'k4-7:4.7 Statusverlauf']],
    ['k5', '5 · Genehmigungsworkflow', ['k5-1:5.1 Flows anlegen', 'k5-2:5.2 Abteilungsfreigabe', 'k5-3:5.3 GF-Freigabe', 'k5-4:5.4 Alle freigeben', 'k5-5:5.5 Flows verwalten', 'k5-6:5.6 Freigaben-Übersicht']],
    ['k6', '6 · Kommentare und Erwähnungen', ['k6-1:6.1 Bestellkommentare', 'k6-2:6.2 Positionskommentare', 'k6-3:6.3 @-Mention']],
    ['k7', '7 · Archiv', ['k7-1:7.1 Archivieren', 'k7-2:7.2 Durchsuchen', 'k7-3:7.3 Wiederherstellen']],
    ['k8', '8 · Stammdaten', ['k8-1:8.1 Artikel', 'k8-2:8.2 Kreditoren', 'k8-3:8.3 Mengeneinheiten', 'k8-4:8.4 BC-Synchronisation']],
    ['k9', '9 · Einstellungen', ['k9-1:9.1 Benutzerprofil', 'k9-2:9.2 Berechtigungen (Admin)', 'k9-3:9.3 Freigabe-Matrix (Admin)', 'k9-4:9.4 E-Mail / SMTP (Admin)', 'k9-5:9.5 Benachrichtigungen (Admin)', 'k9-6:9.6 Dokumenttypen (Admin/Einkauf)', 'k9-7:9.7 Sync-Einstellungen (Admin/Einkauf)']],
    ['k10', '10 · Anhang', ['k10-1:10.1 Statusübersicht', 'k10-2:10.2 FAQ', 'k10-3:10.3 Rollenmatrix']],
  ] : [
    ['k2', '2 · Anmeldung und Navigation', ['k2-1:2.1 Anmeldung', 'k2-2:2.2 Hauptnavigation', 'k2-3:2.3 Benutzermenü und Hilfe']],
    ['k3', '3 · Bestellungen – Übersicht', ['k3-1:3.1 Bestellliste und Filter', 'k3-2:3.2 Neue Bestellung', 'k3-3:3.3 Bestellung übernehmen', 'k3-4:3.4 Massenaktionen']],
    ['k4', '4 · Bestelldetail', ['k4-1:4.1 Bestellkopf', 'k4-2:4.2 Positionen verwalten', 'k4-3:4.3 Position bearbeiten', 'k4-4:4.4 Globale Einstellungen', 'k4-5:4.5 Vertraulich', 'k4-6:4.6 Dokumente', 'k4-7:4.7 Statusverlauf']],
    ['k5', '5 · Genehmigungsworkflow', ['k5-1:5.1 Flows anlegen', 'k5-2:5.2 Abteilungsfreigabe']],
    ['k6', '6 · Kommentare und Erwähnungen', ['k6-1:6.1 Bestellkommentare', 'k6-2:6.2 Positionskommentare', 'k6-3:6.3 @-Mention']],
    ['k7', '7 · Archiv', ['k7-1:7.1 Archivieren', 'k7-2:7.2 Durchsuchen', 'k7-3:7.3 Wiederherstellen']],
    ['k9', '9 · Einstellungen', ['k9-1:9.1 Benutzerprofil']],
  ];

  const tl = (text: string, id: string, sub = false) => (
    <a key={id} style={sub ? S.tocSubLink : S.tocLink} onClick={() => scrollTo(id)}>{text}</a>
  );

  return (
    <>
      {/* ── TOC ── */}
      <div id="toc" style={S.tocHeader}>Inhaltsverzeichnis</div>
      {tocEntries.map(([id, label, subs]: any) => (
        <div key={id} style={S.tocGroup}>
          {tl(label, id)}
          {subs.map((s: string) => {
            const [sid, slabel] = s.split(':');
            return tl(slabel, sid, true);
          })}
        </div>
      ))}

      {/* ══ KAP 1 – nur für privilegierte Rollen ══ */}
      {isPrivileged && <>
        <div id="k1" style={S.chapterHeader}>
          <div style={S.chapterNum}>Kapitel 1</div>
          <h2 style={S.chapterTitle}>Einleitung und Systemüberblick</h2>
        </div>

        <h3 id="k1-1" style={S.h3}>1.1 Zweck und Einsatzbereiche</h3>
        <p>Das <strong>Bestellwesen</strong> ist eine webbasierte Anwendung der Bögra Technologie GmbH zur digitalen Verwaltung interner Beschaffungsvorgänge:</p>
        <ul style={S.ul}>
          <li>Erfassung und Verwaltung von Bestellanforderungen</li>
          <li>Strukturierte Freigabe-Workflows mit abteilungsspezifischen Genehmigern</li>
          <li>Verfolgung des Bestell- und Genehmigungsstatus je Position</li>
          <li>Verwaltung von Artikeln, Kreditoren und Mengeneinheiten</li>
          <li>Kommunikation über Kommentare und @-Erwähnungen</li>
          <li>Integration mit Microsoft Business Central (BC) für Stammdaten</li>
        </ul>

        <h3 id="k1-2" style={S.h3}>1.2 Technische Voraussetzungen</h3>
        <Table
          headers={['Anforderung', 'Beschreibung']}
          rows={[
            ['Browser', 'Microsoft Edge (empfohlen), Chrome, Firefox (aktuell)'],
            ['Netzwerk', 'Internes Firmennetzwerk oder VPN erforderlich'],
            ['Anmeldung', 'Windows-Domänen-Anmeldedaten'],
            ['URL', 'http://bestellwesen.webapps.boegra.com'],
            ['Auflösung', 'Mindestens 1280×800 Pixel empfohlen'],
          ]}
        />
        <InfoBox title="NTLM-Authentifizierung">In manchen Browserkonfigurationen wird die Windows-Identität automatisch übernommen – kein manuelles Eintippen nötig.</InfoBox>

        <h3 id="k1-3" style={S.h3}>1.3 Benutzerrollen und Berechtigungen</h3>
        <Table
          headers={['Rolle', 'Gruppe', 'Beschreibung']}
          rows={[
            ['<strong style="color:#a4262c">Administrator</strong>', 'DGS-Admin', 'Vollzugriff, Benutzer- und Systemverwaltung'],
            ['<strong style="color:#8764b8">Einkauf</strong>', 'DGS-Einkauf', 'Vollzugriff inkl. Stammdaten, Archivierung, Flow-Management'],
            ['<strong style="color:#0078d4">Geschäftsführer</strong>', 'DGS-GeschaeftsFuehrer', 'Management-Level-Freigaben, erweiterte Leseberechtigung'],
            ['Freigeber', 'DGS-Freigaben', 'Abteilungsfreigaben genehmigen oder ablehnen'],
            ['Erstellen & Bearbeiten', 'DGS-Erstellen und bearbeiten', 'Eigene Bestellungen erstellen und bearbeiten'],
            ['Viewer', 'DGS-Viewer', 'Nur lesender Zugriff auf Bestellungen'],
          ]}
        />
        <InfoBox title="Hierarchie:">Administrator &gt; Einkauf &gt; Geschäftsführer &gt; Freigeber &gt; Erstellen &amp; Bearbeiten &gt; Viewer</InfoBox>

        <BackToToc onClick={() => scrollTo('toc')} />
      </>}

      {/* ══ KAP 2 ══ */}
      <div id="k2" style={S.chapterHeader}>
        <div style={S.chapterNum}>Kapitel 2</div>
        <h2 style={S.chapterTitle}>Anmeldung und Navigation</h2>
      </div>

      <h3 id="k2-1" style={S.h3}>2.1 Anmeldung</h3>
      <p>Rufen Sie <strong>http://bestellwesen.webapps.boegra.com</strong> im Browser auf.</p>
      <Placeholder label="Anmeldeseite" desc="Login-Formular mit E-Mail-Feld und Passwortfeld" src="/screenshots/01-anmeldeseite.png" />
      <Steps items={[
        <span>E-Mail im Format <em>vorname.nachname@boegra.com</em> eingeben.</span>,
        'Windows-Domänen-Passwort eingeben.',
        <span>Auf <strong>Anmelden</strong> klicken – Weiterleitung zur Bestellübersicht.</span>,
      ]} />
      <Placeholder label="Startseite / Bestellübersicht nach Anmeldung" desc="Kartenansicht aller aktiven Bestellungen mit Status-Indikatoren" src="/screenshots/02-startseite.png" />
      <TipBox title="Automatische Anmeldung">In Edge mit Intranet-Zone-Konfiguration werden Windows-Anmeldedaten automatisch übermittelt.</TipBox>

      <h3 id="k2-2" style={S.h3}>2.2 Hauptnavigation</h3>
      <Placeholder label="Hauptnavigationsleiste" desc="Blaue Kopfzeile mit Navigationspunkten: Bestellungen, Archiv, Artikel, Kreditoren, Mengeneinheiten, Freigaben" src="/screenshots/03-navigation.png" />
      <Table
        headers={['Menüpunkt', 'Funktion', 'Sichtbar für']}
        rows={[
          ['<strong>Bestellungen</strong>', 'Aktive Bestellungen verwalten', 'Alle Rollen'],
          ['<strong>Archiv</strong>', 'Archivierte Bestellungen einsehen', 'Alle Rollen'],
          ['<strong>Artikel</strong>', 'Artikelstammdaten', 'Alle (Bearbeitung: Admin/Einkauf)'],
          ['<strong>Kreditoren</strong>', 'Lieferantenstammdaten', 'Alle (Bearbeitung: Admin/Einkauf)'],
          ['<strong>Mengeneinheiten</strong>', 'Einheitenstammdaten', 'Alle (Bearbeitung: Admin/Einkauf)'],
          ['<strong>Freigaben</strong>', 'Alle Genehmigungsworkflows', 'Alle Rollen'],
        ]}
      />

      <h3 id="k2-3" style={S.h3}>2.3 Benutzermenü und Hilfe</h3>
      <p>Rechts oben in der Navigationsleiste:</p>
      <ul style={S.ul}>
        <li><strong>? Fragezeichen</strong> – öffnet Hilfe-Menü mit <em>Handbuch</em> (dieses Panel) und <em>Hilfe an/aus</em> (Hover-Tooltips)</li>
        <li><strong>⚙ Zahnrad</strong> – Einstellungen</li>
        <li><strong>Benutzername</strong> – Profil und Abmelden</li>
      </ul>
      <TipBox title="Interaktive Hilfe">Wenn „Hilfe" eingeschaltet ist, erscheint beim Überfahren von Buttons, Feldern und Bereichen ein erklärender Tooltip mit blauer Hervorhebung.</TipBox>

      <BackToToc onClick={() => scrollTo('toc')} />

      {/* ══ KAP 3 ══ */}
      <div id="k3" style={S.chapterHeader}>
        <div style={S.chapterNum}>Kapitel 3</div>
        <h2 style={S.chapterTitle}>Bestellungen – Übersicht</h2>
      </div>

      <h3 id="k3-1" style={S.h3}>3.1 Bestellliste und Filteroptionen</h3>
      <Placeholder label="Bestellübersicht – Kartenansicht" desc="Raster aus Bestellkarten; Filterleiste oben: Alle / Meine / Zu bearbeiten; Suchfeld rechts" src="/screenshots/04-bestelluebersicht.png" />
      <h4 style={S.h4}>Bestellkarte – Elemente</h4>
      <ul style={S.ul}>
        <li><strong>Titel</strong> der Bestellung</li>
        <li><strong>🔒 Schloss-Icon</strong> bei vertraulichen Bestellungen</li>
        <li><strong>Genehmigungsstatus</strong> (farbiger Punkt)</li>
        <li><strong>Bestellstatus</strong> (farbiger Punkt)</li>
        <li>Gesamtsumme in EUR, Antragsteller, Kostenstelle, Erstellungsdatum</li>
        <li><strong>Kreditor(en)</strong> – eindeutige Liste der Lieferanten aller Positionen (je max. 18 Zeichen)</li>
      </ul>
      <Table
        headers={['Filter', 'Beschreibung']}
        rows={[
          ['<strong>Alle</strong>', 'Alle sichtbaren aktiven Bestellungen'],
          ['<strong>Meine</strong>', 'Eigene Bestellungen (Ersteller oder Antragsteller)'],
          ['<strong>Zu bearbeiten</strong>', 'Offene Freigaben oder unbeantwortete @-Erwähnungen'],
        ]}
      />
      <p>Das <strong>Suchfeld</strong> durchsucht: Titel, Bestellnummer, Antragsteller, Kostenstelle, Personalnummer, Artikelname, Art.Nr., Bögra-Nr., Sachkonto, Inventarnummer, <strong>Kreditor/Lieferant</strong>.</p>

      <h3 id="k3-2" style={S.h3}>3.2 Neue Bestellung erstellen</h3>
      <Placeholder label="Dialog Neue Bestellung erstellen" desc="Felder: Titel (Pflicht), Antragsteller (Pflicht), Personalnummer, Kostenstelle, Durchwahl, Hinweistext" src="/screenshots/05-neue-bestellung.png" />
      <Steps items={[
        <span>Auf <strong>„Neue Bestellung"</strong> klicken.</span>,
        <span><strong>Titel</strong> und <strong>Antragsteller</strong> ausfüllen (Pflichtfelder).</span>,
        'Optionale Felder: Personalnummer, Kostenstelle, Durchwahl, Hinweistext.',
        <span><strong>„Speichern"</strong> – Weiterleitung zur Detailansicht.</span>,
      ]} />
      <InfoBox title="Automatische Vorbelegung">Standardwerte aus dem Benutzerprofil (Einstellungen → Profil) werden automatisch ausgefüllt.</InfoBox>

      <h3 id="k3-3" style={S.h3}>3.3 Daten aus alter Bestellung übernehmen</h3>
      <Steps items={[
        <span>Auf <strong>„Neue Bestellung"</strong> klicken.</span>,
        <span>Im Feld <strong>„Daten aus alter Bestellung übernehmen"</strong> eine vorhandene Bestellung suchen und auswählen.</span>,
        'Alle Felder und Positionen werden übernommen und können angepasst werden.',
        <span><strong>„Speichern"</strong>.</span>,
      ]} />

      <h3 id="k3-4" style={S.h3}>3.4 Massenaktionen (Auswahl-Modus)</h3>
      <p>Klicken Sie auf <strong>„Auswählen"</strong>, um den Mehrfachauswahlmodus zu aktivieren.</p>
      <Placeholder label="Bestellübersicht im Auswahl-Modus" desc="Bestellkarten mit Checkboxen; ausgewählte Karten blau umrandet; Aktionsleiste oben sichtbar" src="/screenshots/06-auswahl-modus.png" />
      <Table
        headers={['Aktion', 'Beschreibung', 'Berechtigung']}
        rows={[
          ['<strong>Archivieren</strong>', 'Ausgewählte Bestellungen archivieren', 'Admin, Einkauf'],
          ['<strong>Bestellstatus ändern</strong>', 'Status aller Ausgewählten setzen', 'Admin, Einkauf'],
          ['<strong>Flows → Freigeben</strong>', 'Alle zuständigen Flows genehmigen', 'Freigeber, GF, Admin, Einkauf'],
          ['<strong>Flows → Ablehnen</strong>', 'Alle zuständigen Flows ablehnen', 'Freigeber, GF, Admin, Einkauf'],
          ['<strong>Flows → Entfernen</strong>', 'Alle Flows löschen', 'Admin, Einkauf'],
        ]}
      />
      <WarnBox title="Achtung: Flows entfernen">Unwiderruflich – alle Schritte und Entscheidungen gehen verloren.</WarnBox>

      <BackToToc onClick={() => scrollTo('toc')} />

      {/* ══ KAP 4 ══ */}
      <div id="k4" style={S.chapterHeader}>
        <div style={S.chapterNum}>Kapitel 4</div>
        <h2 style={S.chapterTitle}>Bestelldetail</h2>
      </div>

      <h3 id="k4-1" style={S.h3}>4.1 Bestellkopf und Stammdaten</h3>
      <Placeholder label="Bestelldetail – oberer Bereich" desc="Bestelltitel, Status-Badges (Genehmigung / Bestellung), Werkzeugleiste" src="/screenshots/07-bestelldetail-oben.png" />
      <Table
        headers={['Aktion', 'Beschreibung', 'Berechtigung']}
        rows={[
          ['Bearbeiten', 'Bestellkopfdaten ändern', 'Admin, Einkauf, Ersteller'],
          ['Archivieren', 'Ins Archiv verschieben', 'Admin, Einkauf'],
          ['Alle freigeben', 'Alle offenen eigenen Schritte genehmigen', 'Berechtigte Freigeber'],
          ['Genehmigungsflows', 'Freigabe-Panel öffnen', 'Alle (Aktionen je Rolle)'],
          ['Lieferung melden', 'Lieferung einzelner Positionen oder der Gesamtbestellung erfassen', 'Alle Benutzer'],
        ]}
      />
      <InfoBox title="Lieferung melden">
        Öffnet einen Dialog mit allen Positionen der Bestellung. Entweder einzelne Positionen mit gelieferter Menge ankreuzen oder den Schalter <strong>„Gesamte Bestellung geliefert"</strong> aktivieren, um alle Positionen auf vollständig geliefert zu setzen. Der Vorgang wird im Statusverlauf als <em>„Lieferung gemeldet"</em> mit dem auslösenden Benutzer protokolliert.
      </InfoBox>

      <h3 id="k4-2" style={S.h3}>4.2 Bestellpositionen verwalten</h3>
      <p>Unterhalb des Bestellkopfs befinden sich zwei <strong>Tabs</strong>:</p>
      <Table
        headers={['Tab', 'Inhalt']}
        rows={[
          ['<strong>Positionen</strong>', 'Optionen für alle Positionen · Aktionsleiste · Positionstabelle'],
          ['<strong>Kommentare</strong>', 'Kommentarbereich zur gesamten Bestellung'],
        ]}
      />
      <Placeholder label="Positionstabelle (Tab Positionen)" desc="Spalten: Best.-ID / Pos, BC-Bestellnr., Artikel, Art.Nr., Bögra-Nr., Menge, Einheit, Einzelpreis, Gesamtpreis, Kostenstelle, Sachkonto, Inventarnummer, Liefertermin KW, Genehmigungsstatus, Bestellstatus" src="/screenshots/08-positionstabelle.png" />
      <p>Zeilenaktionen: <strong>Stift</strong> (Bearbeiten) · <strong>Auge</strong> (Ansehen) · <strong>Link</strong> (Artikel-URL öffnen).</p>

      <h3 id="k4-3" style={S.h3}>4.3 Position hinzufügen / bearbeiten</h3>
      <p>Das Positionsformular öffnet sich als Modal mit bis zu <strong>3 Tabs</strong>:</p>
      <Table
        headers={['Tab', 'Inhalt', 'Wann sichtbar']}
        rows={[
          ['<strong>Position</strong>', 'Alle Formularfelder (Artikel, Mengen, Preise, Statusfelder)', 'Immer'],
          ['<strong>Freigabe</strong>', 'Genehmigungsflow-Zuordnung für diese Position', 'Nur bei bestehender Position'],
          ['<strong>Kommentare</strong>', 'Kommentare direkt zu dieser Position', 'Nur bei bestehender Position'],
        ]}
      />
      <Placeholder label="Positionsformular – Tab Position" desc="Zeile 1: Best.-ID/Pos-ID · BC-Bestellnummer · Gesamtsumme; darunter Status, Kostenstelle, Kreditor, Artikel, Mengen, Preise, Beschreibung, Link" />
      <Table
        headers={['Feld', 'Beschreibung', 'Hinweis']}
        rows={[
          ['Best.-ID / Pos-ID', 'Bestellungs-ID und Positions-Nr. kombiniert', 'Nur Anzeige'],
          ['BC-Bestellnummer', 'Bestellnummer in Business Central', 'Nur Admin / Einkauf'],
          ['Artikel', 'Texteingabe mit Such-Dropdown', 'Toggle „inkl. BC" für BC-Artikel'],
          ['Kreditor', 'Texteingabe mit Such-Dropdown', 'Toggle „inkl. BC" für BC-Kreditoren'],
          ['Menge', 'Bestellmenge (Dezimalzahl)', 'Pflichtfeld'],
          ['Mengeneinheit', 'Einheit aus Dropdown', ''],
          ['Einzelpreis', 'Preis pro Einheit EUR', 'Aus Artikelstamm oder manuell'],
          ['Gesamtpreis', 'Menge × Einzelpreis (automatisch)', 'Nur Anzeige'],
          ['Kostenstelle', 'Empfangende Kostenstelle', ''],
          ['Sachkonto', 'Buchungssachkonto', ''],
          ['Inventarnummer', 'Anlagennummer', ''],
          ['Liefertermin KW', 'Gewünschte Lieferwoche', 'Format KW oder KW-JJJJ'],
          ['Lagerortcode', 'BC-Lagerort für diese Position', 'Standard: BÖGRA. Wird mit BC abgeglichen (bidirektional).'],
          ['Offene Menge (lt. BC)', 'Noch ausstehende Menge laut BC', 'Nur Anzeige, wenn vom BC-Sync befüllt'],
        ]}
      />
      <TipBox title="Inline anlegen">Nicht gefundene Artikel/Kreditoren können direkt über „Neu…" im Suchfeld erstellt werden.</TipBox>

      <h3 id="k4-4" style={S.h3}>4.4 Globale Einstellungen (alle Positionen)</h3>
      <p>Im Tab <strong>„Positionen"</strong> befindet sich oben der aufklappbare Bereich <strong>„Optionen für alle Positionen"</strong>.</p>
      <Placeholder label="Globale Einstellungen" desc="Aufgeklappter Bereich im Tab Positionen; Felder für Liefertermin, Genehmigungsstatus, Bestellstatus, Kreditor, Kostenstelle, Sachkonto, Inventar; sofortige Wirkung" src="/screenshots/09-globale-einstellungen.png" />
      <Table
        headers={['Zeile', 'Felder']}
        rows={[
          ['Zeile 1', 'Liefertermin KW · Lagerortcode'],
          ['Zeile 2', 'Genehmigungsstatus · Bestellstatus'],
        ]}
      />
      <Steps items={[
        <span>Tab <strong>„Positionen"</strong> öffnen.</span>,
        <span>Auf <strong>„Optionen für alle Positionen"</strong> klicken (klappt auf).</span>,
        'Gewünschte Werte eintragen – Änderungen werden sofort ohne Seitenneuladen auf alle Positionen angewendet.',
      ]} />
      <InfoBox title="Lagerortcode">Setzt den BC-Lagerort für alle Positionen. Standard ist „BÖGRA". Nur sinnvoll wenn Positionen in einen abweichenden BC-Lagerort gebucht werden sollen.</InfoBox>

      <h3 id="k4-5" style={S.h3}>4.5 Vertrauliche Bestellungen</h3>
      <p>Vertrauliche Bestellungen sind nur sichtbar für: Admin, Einkauf, GF, Ersteller, eingetragene Freigeber, per @-Erwähnung genannte Personen.</p>
      <Steps items={[
        'Bestelldetail öffnen.',
        <span>Auf <strong>🔒 Vertraulich</strong>-Toggle im Bestellkopf klicken.</span>,
        'Änderung wird sofort gespeichert.',
      ]} />

      <h3 id="k4-6" style={S.h3}>4.6 Dokumente</h3>
      <p>Der Tab <strong>„Dokumente"</strong> im Bestelldetail ermöglicht das Anhängen von Dateien direkt an eine Bestellung oder an einzelne Positionen (Mini-DMS). Alle Dokumente werden in der Datenbank gespeichert.</p>
      <Placeholder label="Tab Dokumente – Upload-Bereich und Dokumentenliste" desc="Oben: Datei-Auswahl-Button, Typ-Dropdown, Positions-Dropdown, Hochladen-Button. Unten: Dokumentenliste mit Typ-Badge, Positions-Badge, Dateiname als Download-Link, Größe, Hochlader und Datum, Löschen-Icon." src="/screenshots/25-dokumente-tab.png" />

      <h4 style={S.h4}>Dokument hochladen</h4>
      <Steps items={[
        <span>Tab <strong>„Dokumente"</strong> öffnen.</span>,
        <span>Datei in die <strong>Drag &amp; Drop Zone</strong> ziehen — oder in die Zone klicken und Datei auswählen (max. 20 MB). Die Zone hebt sich blau hervor, wenn eine Datei darüber gehalten wird; der Browser öffnet die Datei dabei nicht.</span>,
        <span>Optional: <strong>Dokumenttyp</strong> wählen (z.B. Rechnung, Lieferschein).</span>,
        <span>Optional: <strong>Zuordnung zu einer Position</strong> wählen – sonst gilt das Dokument für die gesamte Bestellung.</span>,
        <span><strong>„Hochladen"</strong> klicken.</span>,
      ]} />

      <h4 style={S.h4}>Dokument herunterladen / öffnen</h4>
      <p>Klicken Sie auf den <strong>Dateinamen</strong> in der Liste – das Dokument öffnet sich in einem neuen Browser-Tab.</p>

      <h4 style={S.h4}>Dokument löschen</h4>
      <p>Klicken Sie auf das <strong>Papierkorb-Icon</strong> rechts neben dem Dokument und bestätigen Sie die Abfrage.</p>
      <InfoBox title="Berechtigungen">
        Hochladen: Mind. <em>Erstellen &amp; Bearbeiten</em>.<br />
        Löschen: Eigene Dokumente (jeder Hochlader) oder Admin / Einkauf für alle.
      </InfoBox>

      <h4 style={S.h4}>Dokumenttypen</h4>
      <Table
        headers={['Typ', 'Farbe', 'System']}
        rows={[
          ['Rechnung', 'Blau (#0078d4)', 'Ja – nicht löschbar'],
          ['Lieferschein', 'Grün (#107c10)', 'Ja – nicht löschbar'],
          ['Angebot', 'Violett (#8764b8)', 'Nein'],
          ['Sonstige', 'Grau (#666666)', 'Ja – nicht löschbar'],
        ]}
      />
      <TipBox title="Eigene Typen">Admin und Einkauf können in <em>Einstellungen → Dokumenttypen</em> weitere Typen anlegen und vorhandene bearbeiten oder löschen.</TipBox>

      <h3 id="k4-7" style={S.h3}>4.7 Statusverlauf</h3>
      <p>Der Tab <strong>„Statusverlauf"</strong> zeigt alle relevanten Ereignisse einer Bestellung chronologisch in einer Tabelle (neuestes Ereignis zuerst).</p>
      <Placeholder label="Tab Statusverlauf – Ereignistabelle" desc="Tabelle: Datum, Ereignis (Icon + farbiger Text), Benutzer, Details (Kommentar, Abteilung), Position. Zeilen: Bestellung erstellt, Freigabe angefordert, Freigegeben/Abgelehnt, In BC übertragen, Lieferung gemeldet, Teilweise/Vollständig geliefert." src="/screenshots/26-statusverlauf.png" />
      <Table
        headers={['Ereignis', 'Symbol', 'Benutzer', 'Wann']}
        rows={[
          ['Bestellung erstellt', '📋 Blau', 'Ersteller', 'Beim Anlegen der Bestellung'],
          ['Freigabe angefordert', '🔄 Violett', 'Flow-Ersteller', 'Wenn ein Freigabe-Flow für eine Position angelegt wird'],
          ['Freigegeben', '✅ Grün', 'Genehmigender', 'Wenn ein Freigabeschritt genehmigt wird (inkl. Kommentar)'],
          ['Abgelehnt', '❌ Rot', 'Ablehnender', 'Wenn ein Freigabeschritt abgelehnt wird (inkl. Kommentar)'],
          ['In BC übertragen', '📤 Blau', 'Auslösender Benutzer', 'Wenn „In BC anlegen" ausgeführt wird'],
          ['Lieferung gemeldet', '🚚 Grün', 'Meldender Benutzer', 'Wenn „Lieferung melden" verwendet wird'],
          ['Vollständig geliefert', '📦 Grün', 'System, automatisch', 'Wenn offene Menge lt. BC = 0 (vom BC-Sync gesetzt)'],
          ['Teilweise geliefert', '🔸 Hellgrün', 'System, automatisch', 'Wenn offene Menge lt. BC > 0 (vom BC-Sync gesetzt)'],
        ]}
      />
      <InfoBox title="Benutzer im Statusverlauf">
        Manuelle Aktionen (Bestellung erstellen, Freigaben, In BC anlegen, Lieferung melden) zeigen den auslösenden Benutzer. Automatisch durch den BC-Sync ausgelöste Ereignisse (Lieferstatus-Aktualisierungen) zeigen <strong>„System, automatisch"</strong>.
      </InfoBox>
      <InfoBox title="Zeitstempel">Lieferstatus-Ereignisse (Vollständig/Teilweise geliefert) können ohne exakten Zeitstempel sein – sie werden vom nächtlichen BC-Sync befüllt und am Ende der Tabelle angezeigt.</InfoBox>

      <BackToToc onClick={() => scrollTo('toc')} />

      {/* ══ KAP 5 ══ */}
      <div id="k5" style={S.chapterHeader}>
        <div style={S.chapterNum}>Kapitel 5</div>
        <h2 style={S.chapterTitle}>Genehmigungsworkflow</h2>
      </div>

      <h3 id="k5-1" style={S.h3}>5.1 Genehmigungsflows anlegen</h3>
      <Placeholder label="Genehmigungsflow-Panel" desc="Positionsgruppen; je Position Abteilungs-Flows (Aktionsbereich oben) und eine gemeinsame GF-Box; oben Abteilung hinzufügen (alle Positionen)" src="/screenshots/10-genehmigungsflow-panel.png" />
      <h4 style={S.h4}>Flow für alle Positionen anlegen</h4>
      <Steps items={[
        'Abteilung im oberen Dropdown wählen.',
        <span><strong>„Flow erstellen"</strong> – je Position wird ein Flow dieser Abteilung angelegt.</span>,
      ]} />
      <h4 style={S.h4}>Zusätzliche Abteilung für einzelne Position</h4>
      <Steps items={[
        'Zur gewünschten Position scrollen.',
        <span><strong>„+ Abteilung hinzufügen"</strong> anklicken.</span>,
        <span>Abteilung wählen und <strong>„Flow erstellen"</strong>.</span>,
      ]} />
      <InfoBox title="Dynamische Freigabe-Matrix">Genehmiger werden bei jedem Aufruf aus der aktuellen Matrix geladen. Änderungen gelten sofort für alle Flows.</InfoBox>

      <h3 id="k5-2" style={S.h3}>5.2 Abteilungsfreigabe</h3>
      <Placeholder label="Aktionsbereich Abteilungsfreigabe" desc="Oben im Flow-Abschnitt: Kommentarfeld, grüner Freigeben-Button, roter Ablehnen-Button; darunter Genehmiger-Liste mit Status" src="/screenshots/11-abteilungsfreigabe.png" />
      <p>Freigabe über das Genehmigungsflow-Panel (Werkzeugleiste) <em>oder</em> direkt im Tab <strong>„Freigabe"</strong> des Positionsformulars:</p>
      <Steps items={[
        <span>Position öffnen → Tab <strong>„Freigabe"</strong> wählen, oder Genehmigungsflow-Panel über die Werkzeugleiste öffnen.</span>,
        'Zum eigenen Flow scrollen.',
        'Optional Kommentar eingeben.',
        <span><strong>„Freigeben"</strong> oder <strong>„Ablehnen"</strong> klicken.</span>,
      ]} />

      {isPrivileged && <>
        <h3 id="k5-3" style={S.h3}>5.3 Geschäftsführungsfreigabe</h3>
        <p>Pro Position eine gemeinsame <strong>GF-Box</strong> mit allen GF-Freigaben – unabhängig von der Zahl der Abteilungs-Flows.</p>
        <Placeholder label="GF-Box pro Position" desc="Box Geschäftsführung mit Kommentarfeld und Freigeben/Ablehnen oben; darunter alle GF-Genehmiger mit Statusanzeige" src="/screenshots/11b-gf-box.png" />

        <h3 id="k5-4" style={S.h3}>5.4 Alle Positionen auf einmal freigeben</h3>
        <Steps items={[
          <span>In der Werkzeugleiste <strong>„Alle freigeben"</strong> klicken.</span>,
          'Optional Kommentar eingeben.',
          <span><strong>„Bestätigen"</strong> – alle offenen eigenen Schritte werden genehmigt.</span>,
        ]} />

        <h3 id="k5-5" style={S.h3}>5.5 Flows verwalten</h3>
        <p>Einzelnen Flow löschen: Im Panel auf das <strong>🗑 Papierkorb-Icon</strong> rechts neben dem Abteilungsnamen klicken (Admin/Einkauf).</p>
        <p>Mehrere Flows über den Auswahl-Modus in der Bestellübersicht verwalten (Kapitel 3.4).</p>
        <WarnBox>Gelöschte Flows können nicht wiederhergestellt werden.</WarnBox>

        <h3 id="k5-6" style={S.h3}>5.6 Freigaben-Übersichtsseite</h3>
        <Placeholder label="Freigaben-Übersichtsseite" desc="Tabelle mit Bestellung, Artikel, Status, Letzte Aktion, Abteilung; offene eigene Schritte oben sortiert; Suchfeld; Auswahl-Modus" src="/screenshots/12-freigaben-uebersicht.png" />
        <ul style={S.ul}>
          <li>Eigene offene Flows werden automatisch <strong>nach oben sortiert</strong>.</li>
          <li>Im Auswahl-Modus: mehrere Flows gleichzeitig freigeben, ablehnen oder löschen.</li>
        </ul>
      </>}

      <BackToToc onClick={() => scrollTo('toc')} />

      {/* ══ KAP 6 ══ */}
      <div id="k6" style={S.chapterHeader}>
        <div style={S.chapterNum}>Kapitel 6</div>
        <h2 style={S.chapterTitle}>Kommentare und Erwähnungen</h2>
      </div>

      <h3 id="k6-1" style={S.h3}>6.1 Bestellkommentare</h3>
      <p>Bestellkommentare sind im Tab <strong>„Kommentare"</strong> in der Bestelldetailansicht zu finden.</p>
      <Placeholder label="Tab Kommentare in Bestelldetail" desc="Texteingabefeld; vorhandene Kommentare mit Name, Datum, Text, Antwort-Links, Löschen-Icons" src="/screenshots/13-kommentare-leer.png" />
      <Placeholder label="Kommentar mit Antwort-Funktion" src="/screenshots/15-kommentar-vorhanden.png" />
      <Steps items={[
        <span>Im Bestelldetail Tab <strong>„Kommentare"</strong> öffnen.</span>,
        'Kommentar eintippen.',
        <span><strong>Enter</strong> oder <strong>„Senden"</strong>.</span>,
      ]} />
      <InfoBox title="Kommentar bearbeiten">Eigene Kommentare können innerhalb von <strong>15 Minuten</strong> nach dem Senden bearbeitet werden – über den blauen Bearbeiten-Button. Admin und Einkauf können Kommentare ohne Zeitbeschränkung bearbeiten.</InfoBox>

      <h3 id="k6-2" style={S.h3}>6.2 Positionskommentare</h3>
      <p>Positionskommentare befinden sich im Tab <strong>„Kommentare"</strong> des Positionsformulars (nur bei bestehenden Positionen sichtbar). Kommentare werden mit farbigem Positions-Badge angezeigt (z.B. „Pos. 3"). Auch hier gilt: eigene Kommentare sind innerhalb von 15 Minuten editierbar.</p>
      <Steps items={[
        <span>Position öffnen (Stift- oder Auge-Icon in der Tabelle).</span>,
        <span>Tab <strong>„Kommentare"</strong> wählen.</span>,
        'Kommentar eingeben und senden.',
      ]} />

      <h3 id="k6-3" style={S.h3}>6.3 Personen erwähnen (@-Mention)</h3>
      <Placeholder label="@-Mention Autocomplete" desc="Texteingabe @mül mit erscheinender Dropdown-Auswahlliste für Benutzer" src="/screenshots/14-mention-autocomplete.png" />
      <Steps items={[
        <span><strong>@</strong> + mind. 2 Zeichen des Namens eingeben.</span>,
        'Person aus Liste wählen.',
        'Name wird als Link eingefügt.',
      ]} />
      <InfoBox title="Auswirkung">Erwähnte Personen erhalten Sichtbarkeit auf die Bestellung (auch bei vertraulich) und sehen sie unter „Zu bearbeiten".</InfoBox>

      <BackToToc onClick={() => scrollTo('toc')} />

      {/* ══ KAP 7 ══ */}
      <div id="k7" style={S.chapterHeader}>
        <div style={S.chapterNum}>Kapitel 7</div>
        <h2 style={S.chapterTitle}>Archiv</h2>
      </div>

      <h3 id="k7-1" style={S.h3}>7.1 Bestellungen archivieren</h3>
      <p>Nur <strong style={{ color: '#a4262c' }}>Administrator</strong> und <strong style={{ color: '#8764b8' }}>Einkauf</strong>.</p>
      <p><strong>Einzeln:</strong> Bestelldetail → „Archivieren" → Bestätigen.</p>
      <p><strong>Mehrere:</strong> Auswahl-Modus in Bestellübersicht → auswählen → „Archivieren".</p>

      <h3 id="k7-2" style={S.h3}>7.2 Archiv durchsuchen</h3>
      <Placeholder label="Archiv-Übersicht" desc="Kartenansicht archivierter Bestellungen; Archiv-Symbol auf Karten; Suchfeld und Filterleiste" src="/screenshots/16-archiv.png" />
      <p>Archiv-Kacheln zeigen wie die aktiven Bestellungen farbige <strong>Kommentar-Badges</strong> rechts oben (blau = neue Kommentare, grau = gelesen).</p>

      <h3 id="k7-3" style={S.h3}>7.3 Bestellungen wiederherstellen</h3>
      <Steps items={[
        'Archiv-Seite öffnen.',
        'Auswahl-Modus aktivieren und Bestellungen auswählen.',
        <span><strong>„Wiederherstellen"</strong> klicken.</span>,
      ]} />

      <BackToToc onClick={() => scrollTo('toc')} />

      {/* ══ KAP 8 – nur für privilegierte Rollen ══ */}
      {isPrivileged && <>
        <div id="k8" style={S.chapterHeader}>
          <div style={S.chapterNum}>Kapitel 8</div>
          <h2 style={S.chapterTitle}>Stammdaten verwalten</h2>
        </div>
        <p>Bearbeitung nur für <strong style={{ color: '#a4262c' }}>Administrator</strong> und <strong style={{ color: '#8764b8' }}>Einkauf</strong>.</p>

        <h3 id="k8-1" style={S.h3}>8.1 Artikel</h3>
        <Placeholder label="Artikelliste" desc="Tabelle mit Artikelname, Art.Nr., Bögra-Nr., Mengeneinheit, Einzelpreis, Beschreibung; Suchfeld; Neuer Artikel- und Auswählen-Buttons" src="/screenshots/17-artikelliste.png" />
        <Steps items={[
          <span>Auf <strong>„Neuer Artikel"</strong> klicken.</span>,
          <span>Pflicht: <strong>Artikelname</strong> und <strong>Artikelnummer</strong>.</span>,
          'Optional: Bögra-Nr., Mengeneinheit, Preis, Beschreibung, Hyperlink.',
          <span><strong>„Speichern"</strong>.</span>,
        ]} />

        <h3 id="k8-2" style={S.h3}>8.2 Kreditoren</h3>
        <Placeholder label="Kreditorenliste" desc="Tabelle mit Kreditorname, Kreditorennummer, Kontaktperson, E-Mail, Telefon; Suchfeld; CRUD-Buttons" src="/screenshots/18-kreditorenliste.png" />
        <Steps items={[
          <span><strong>„Neuer Kreditor"</strong>.</span>,
          <span>Pflicht: <strong>Kreditorname</strong>.</span>,
          'Optional: Nr., Kontakt, E-Mail, Telefon, Adresse.',
          <span><strong>„Speichern"</strong>.</span>,
        ]} />
        <InfoBox title="BC-Integration">Kreditoren mit Kreditorennummer werden mit Business Central abgeglichen. Toggle „inkl. BC" im Positionsformular filtert darauf.</InfoBox>

        <h3 id="k8-3" style={S.h3}>8.3 Mengeneinheiten</h3>
        <Table
          headers={['Feld', 'Beschreibung', 'Beispiel']}
          rows={[
            ['Einheit', 'Vollständige Bezeichnung', 'Stück'],
            ['Kurzform', 'Abkürzung', 'Stk'],
            ['Kennzahl', 'BC-Code (Pflichtfeld)', 'STK'],
          ]}
        />

        <h3 id="k8-4" style={S.h3}>8.4 Datensynchronisation mit Business Central</h3>
        <Placeholder label="Einstellungen – Tab Datensynchronisation" desc="Drei Bereiche: Artikel, Kreditoren, Mengeneinheiten; je Synchronisieren-Button und letzter Sync-Zeitstempel" src="/screenshots/19-sync.png" />
        <Steps items={[
          'Einstellungen öffnen (Zahnrad-Icon).',
          <span>Tab <strong>„Datensynchronisation"</strong> wählen.</span>,
          <span>Für Artikel / Kreditoren / Mengeneinheiten / Lagerorte auf <strong>„Synchronisieren"</strong> klicken.</span>,
        ]} />
        <InfoBox title="Lagerorte">BC-Lagerorte (Tabelle „Location") werden automatisch synchronisiert und stehen im Positionsformular als Dropdown zur Verfügung. Standard-Lagerort: <strong>BÖGRA</strong>. Der Lagerortcode wird beim Anlegen in BC übergeben und beim Lieferstatus-Sync zurückgelesen.</InfoBox>
        <InfoBox title="Sync-Protokoll">Jeder Sync-Lauf wird im Tab „Synchronisation" unter dem Bereich „Sync-Protokoll" aufgezeichnet – inklusive Typ (Artikel, Lieferstatus, <strong>Auto-Archivierung</strong> …), Ergebnis (erstellt / aktualisiert / übersprungen / Fehler) und Uhrzeit.</InfoBox>

        <BackToToc onClick={() => scrollTo('toc')} />
      </>}

      {/* ══ KAP 9 ══ */}
      <div id="k9" style={S.chapterHeader}>
        <div style={S.chapterNum}>Kapitel 9</div>
        <h2 style={S.chapterTitle}>Einstellungen</h2>
      </div>
      <p>Öffnen: Klick auf <strong>⚙ Zahnrad-Icon</strong> rechts oben.</p>

      <h3 id="k9-1" style={S.h3}>9.1 Benutzerprofil</h3>
      <Placeholder label="Einstellungen – Tab Profil" desc="Felder: Personalnummer, Anfordernde Kostenstelle, Durchwahl; Speichern-Button; Rollenanzeige (read-only)" src="/screenshots/20-profil.png" />
      <Table
        headers={['Feld', 'Beschreibung']}
        rows={[
          ['Personalnummer', 'Wird automatisch in neue Bestellungen eingetragen'],
          ['Kostenstelle', 'Standard-Kostenstelle für neue Bestellungen'],
          ['Durchwahl', 'Telefon-Durchwahl für Bestellungen'],
        ]}
      />

      {isPrivileged && <>
        <h3 id="k9-2" style={S.h3}>9.2 Berechtigungsverwaltung (Admin)</h3>
        <p>Nur <strong style={{ color: '#a4262c' }}>Administratoren</strong>.</p>
        <Placeholder label="Einstellungen – Tab Berechtigungen" desc="Dropdown Gruppenauswahl; Mitgliederliste mit Löschen-Icons; Suchfeld zum Hinzufügen" src="/screenshots/21-berechtigungen.png" />
        <Steps items={[
          'Gruppe im Dropdown wählen.',
          'Im Suchfeld mind. 2 Zeichen des Benutzernamens eingeben.',
          <span>Benutzer auswählen und <strong>„Hinzufügen"</strong>.</span>,
        ]} />

        <h3 id="k9-3" style={S.h3}>9.3 Freigabe-Matrix (Admin)</h3>
        <p>Nur <strong style={{ color: '#a4262c' }}>Administratoren</strong>. Definiert, wer für welche Abteilung Bestellungen genehmigen darf.</p>
        <Placeholder label="Einstellungen – Tab Freigabe-Matrix" desc="Liste der Abteilungseinträge mit Genehmigern; Eintrag hinzufügen-Button; Bearbeiten/Löschen je Eintrag" src="/screenshots/22-freigabe-matrix.png" />
        <Steps items={[
          <span><strong>„Eintrag hinzufügen"</strong>.</span>,
          'Abteilung wählen, Genehmiger suchen und hinzufügen.',
          <span><strong>„Speichern"</strong>.</span>,
        ]} />
        <InfoBox title="Dynamisch">Änderungen gelten sofort für alle bestehenden und neuen Flows. Geschäftsführer werden über den Eintrag „Geschäftsführung" verwaltet.</InfoBox>

        <h3 id="k9-4" style={S.h3}>9.4 E-Mail / SMTP (Admin)</h3>
        <p>Nur <strong style={{ color: '#a4262c' }}>Administratoren</strong>. Konfiguriert den ausgehenden SMTP-Server für zukünftige E-Mail-Benachrichtigungen.</p>
        <Placeholder label="Einstellungen – Tab E-Mail / SMTP" desc="Voreinstellungs-Buttons, SMTP-Server, Port, SSL-Checkbox, Benutzername, Passwort, Absender-E-Mail, Absender-Name, Speichern- und Test-Mail-Button" src="/screenshots/23-email-smtp.png" />
        <Table
          headers={['Feld', 'Beschreibung', 'Beispiel']}
          rows={[
            ['SMTP-Server', 'Hostname des Mailservers', 'smtp.office365.com'],
            ['Port', 'SMTP-Port', '587 (STARTTLS) / 465 (SSL) / 25 (intern)'],
            ['SSL/TLS / STARTTLS', 'Verschlüsselung aktivieren', 'Aktiv für Office 365 und Port 587'],
            ['Benutzername', 'Anmeldekonto am Mailserver', 'it-support@boegra.com'],
            ['Passwort', 'SMTP-Passwort (verschlüsselt gespeichert)', ''],
            ['Absender-E-Mail', 'From-Adresse in gesendeten Mails', 'it-support@boegra.com'],
            ['Absender-Name', 'Anzeigename des Absenders', 'Bögra-Bestellwesen'],
          ]}
        />
        <h4 style={S.h4}>Voreinstellungen</h4>
        <Table
          headers={['Preset', 'Server', 'Port', 'TLS', 'Verwendung']}
          rows={[
            ['Office 365', 'smtp.office365.com', '587', 'STARTTLS', 'Exchange Online / Microsoft 365'],
            ['Smarthost intern', 'rdp', '25', 'Kein', 'Interner Relay-Server (kein Auth nötig)'],
          ]}
        />
        <Steps items={[
          <span>Tab <strong>„E-Mail"</strong> in den Einstellungen öffnen.</span>,
          <span>Optional Voreinstellung per Button wählen (<em>„Office 365"</em> oder <em>„Smarthost intern"</em>).</span>,
          'SMTP-Server, Port und Zugangsdaten eintragen.',
          <span><strong>„Speichern"</strong> klicken.</span>,
          <span>Mit <strong>„Test-Mail senden"</strong> die Konfiguration prüfen – Testmail geht an die eingetragene Absender-E-Mail.</span>,
        ]} />
        <InfoBox title="Passwort-Handling">Das Passwort wird verschlüsselt in der Datenbank gespeichert und nie im Klartext angezeigt (Platzhalter ••••••••). Ein neues Passwort wird nur gespeichert, wenn das Feld geändert wird.</InfoBox>

        <h3 id="k9-5" style={S.h3}>9.5 Benachrichtigungen und E-Mail-Templates (Admin)</h3>
        <p>Nur <strong style={{ color: '#a4262c' }}>Administratoren</strong>. Das System versendet automatisch E-Mails bei wichtigen Ereignissen. Inhalt und Betreff der Mails können über Templates individuell angepasst werden.</p>
        <Placeholder label="Einstellungen – Tab E-Mail / Abschnitt Benachrichtigungen" desc="Drei Template-Kacheln: Freigabe-Anforderung, Freigabe-Entscheidung, Position geändert; je Template: Betreff-Feld, Body-Editor (HTML), Vorschau-Panel mit aufgelösten Variablen, Speichern-Button" src="/screenshots/24-benachrichtigungen.png" />

        <h4 style={S.h4}>Welche E-Mails werden versendet?</h4>
        <Table
          headers={['Ereignis', 'Empfänger', 'Logik']}
          rows={[
            ['<strong>Freigabe-Anforderung</strong><br/><em>freigabe_anforderung</em>', 'Zuständige Freigeber / GF', 'Pro Freigeber eine E-Mail wenn Flow(s) zugeordnet werden. Digest: 5-Min.-Wartefenster, alle Positionen einer Bestellung werden zusammengefasst. Gelöschte Flows innerhalb des Fensters werden übersprungen.'],
            ['<strong>Freigabe-Entscheidung</strong><br/><em>freigabe_entscheidung</em>', 'Einkauf (EinkaufEmail) + Ersteller der Bestellung', 'Wenn ein Flow freigegeben oder abgelehnt wird. Digest: alle Entscheidungen in einer Bestellung innerhalb von 5 Min. in einer Mail.'],
            ['<strong>Position geändert</strong><br/><em>position_geaendert</em>', 'Einkauf (EinkaufEmail)', 'Wenn eine Bestellposition geändert wird. Digest: alle Positionsänderungen in einer Bestellung innerhalb von 5 Min. in einer Mail.'],
          ]}
        />

        <h4 style={S.h4}>Digest-Logik (5-Minuten-Fenster)</h4>
        <p>Damit nicht bei jedem Klick eine separate Mail herausgeht, werden Ereignisse gesammelt: Sobald 5 Minuten lang keine weitere Aktivität in der gleichen Bestellung stattfindet, wird die Sammelmail versendet. Bei erneuter Aktivität innerhalb der 5 Minuten startet das Fenster neu.</p>
        <InfoBox title="Letzte Änderung gewinnt">Bei Freigabe-Anforderungen: Wird ein Flow innerhalb des Fensters gelöscht und ein anderer neu zugeordnet, enthält die Mail nur den aktuell gültigen Flow.</InfoBox>

        <h4 style={S.h4}>Sonderfall: Neue Bestellung (10-Minuten-Schutzfenster)</h4>
        <p>Positionen, die innerhalb der ersten <strong>10 Minuten</strong> nach Anlage einer Bestellung hinzugefügt oder geändert werden, lösen <em>keine</em> „Position geändert"-Mail aus. Stattdessen wird der Event-Typ auf <strong>„Neue Bestellung erstellt"</strong> gesetzt — der Einkauf erhält also nur eine einzige zusammengefasste Benachrichtigung über die neue Bestellung, nicht für jede Positionskorrektur einzeln.</p>
        <TipBox title="Warum 10 Minuten?">Das gibt dem Ersteller genug Zeit, Tippfehler zu korrigieren und weitere Positionen hinzuzufügen, bevor der Digest abgeschickt wird — ohne dass der Einkauf einen Regen von „Position geändert"-Mails bekommt.</TipBox>

        <h4 style={S.h4}>Template-Variablen</h4>
        <Table
          headers={['Variable', 'Beschreibung', 'Verfügbar in']}
          rows={[
            ['<code>{{Bestellung.Titel}}</code>', 'Titel der Bestellung', 'Alle'],
            ['<code>{{Bestellung.Antragsteller}}</code>', 'Name des Antragstellers', 'Alle'],
            ['<code>{{Bestellung.Link}}</code>', 'Klickbarer Link zur Bestellung', 'Alle'],
            ['<code>{{Anforderungen.Liste}}</code>', 'HTML-Tabelle aller angeforderter Freigaben', 'freigabe_anforderung'],
            ['<code>{{Anforderungen.Anzahl}}</code>', 'Anzahl der Anforderungen', 'freigabe_anforderung'],
            ['<code>{{Anforderung.Abteilung}}</code>', 'Abteilungsname', 'freigabe_anforderung'],
            ['<code>{{Entscheidungen.Liste}}</code>', 'HTML-Tabelle der Entscheidungen', 'freigabe_entscheidung'],
            ['<code>{{Entscheidungen.Anzahl}}</code>', 'Anzahl der Entscheidungen', 'freigabe_entscheidung'],
            ['<code>{{Aenderungen.Liste}}</code>', 'HTML-Tabelle der Positionsänderungen', 'position_geaendert'],
            ['<code>{{Aenderungen.Anzahl}}</code>', 'Anzahl der Änderungen', 'position_geaendert'],
          ]}
        />

        <h4 style={S.h4}>EinkaufEmail konfigurieren</h4>
        <p>Die Sammel-Mailadresse des Einkaufs (Empfänger für Freigabe-Entscheidungen und Positionsänderungen) wird im Tab <strong>„E-Mail"</strong> im Feld <strong>„Einkauf-E-Mail"</strong> hinterlegt.</p>
        <Steps items={[
          <span>Einstellungen öffnen → Tab <strong>„E-Mail"</strong>.</span>,
          <span>Im Feld <strong>„Einkauf-E-Mail"</strong> die gewünschte Adresse eintragen.</span>,
          <span><strong>„Speichern"</strong>.</span>,
        ]} />

        <h3 id="k9-6" style={S.h3}>9.6 Dokumenttypen verwalten (Admin / Einkauf)</h3>
        <p><strong style={{ color: '#a4262c' }}>Admin</strong> und <strong style={{ color: '#8764b8' }}>Einkauf</strong> können in den Einstellungen eigene Dokumenttypen anlegen, bearbeiten und löschen.</p>
        <Placeholder label="Einstellungen – Tab Dokumenttypen" desc="Liste vorhandener Typen mit farbigem Badge, Name, Reihenfolge; Bearbeiten- und Löschen-Icons; Formular zum Anlegen neuer Typen (Name, Farbe, Reihenfolge)" src="/screenshots/27-dokumenttypen.png" />

        <h4 style={S.h4}>Typen anlegen</h4>
        <Steps items={[
          <span>Tab <strong>„Dokumenttypen"</strong> öffnen.</span>,
          'Name eingeben.',
          'Farbe wählen (Farbwähler).',
          'Reihenfolge festlegen (kleine Zahl = weiter oben in Auswahl).',
          <span><strong>„Anlegen"</strong>.</span>,
        ]} />
        <WarnBox title="System-Typen">Die vordefinierten Typen <em>Rechnung</em>, <em>Lieferschein</em> und <em>Sonstige</em> sind System-Typen und können nicht gelöscht werden. Sie können jedoch umbenannt und eingefärbt werden (sofern nicht als IstSystem markiert).</WarnBox>

        <h3 id="k9-7" style={S.h3}>9.7 Sync-Einstellungen (Admin / Einkauf)</h3>
        <p><strong style={{ color: '#a4262c' }}>Admin</strong> und <strong style={{ color: '#8764b8' }}>Einkauf</strong> können im Tab „Synchronisation" die automatische Archivierung nach Lieferung konfigurieren.</p>

        <h4 style={S.h4}>Automatische Archivierung nach Lieferung</h4>
        <p>
          Das System kann vollständig gelieferte Bestellungen nach einer konfigurierbaren Wartezeit automatisch archivieren.
          Die Prüfung erfolgt nach jedem stündlichen Lieferstatus-Sync.
        </p>
        <Table
          headers={['Feld', 'Beschreibung']}
          rows={[
            ['Tage nach Wareneingang', 'Anzahl der Tage ab dem Datum des letzten BC-Wareneingangs (BCLieferdatum), nach denen eine Bestellung automatisch archiviert wird. Leer lassen = deaktiviert.'],
          ]}
        />
        <Steps items={[
          <span>Einstellungen öffnen → Tab <strong>„Synchronisation"</strong>.</span>,
          <span>Block <strong>„Automatische Archivierung nach Lieferung"</strong> (hellblauer Rahmen) aufrufen.</span>,
          'Anzahl Tage eingeben (z. B. 30 = 30 Tage nach Wareneingang).',
          <span><strong>„Speichern"</strong> – Bestätigung erscheint als Statusmeldung.</span>,
        ]} />
        <InfoBox title="Bedingungen für die automatische Archivierung">
          Eine Bestellung wird archiviert, wenn <strong>alle Positionen</strong> den Status „Geliefert" haben <strong>und</strong> das neueste Lieferdatum mindestens die eingestellte Anzahl Tage zurückliegt.
          Als Lieferdatum gilt: <strong>BCLieferdatum</strong> (vom BC-Sync gesetzt) falls vorhanden, sonst das <strong>Änderungsdatum</strong> der Position (= Zeitpunkt des manuellen Status-Wechsels auf „Geliefert").
          Damit funktioniert die Auto-Archivierung sowohl für BC-Bestellungen als auch für rein manuell verwaltete Bestellungen.
        </InfoBox>
        <InfoBox title="Sync-Protokoll">
          Jede Auto-Archivierungs-Ausführung wird im Sync-Protokoll unter dem Typ <strong>„Auto-Archivierung"</strong> aufgezeichnet – mit Anzahl archivierter Bestellungen und etwaigen Fehlern.
        </InfoBox>
      </>}

      <BackToToc onClick={() => scrollTo('toc')} />

      {/* ══ KAP 10 – nur für privilegierte Rollen ══ */}
      {isPrivileged && <>
        <div id="k10" style={S.chapterHeader}>
          <div style={S.chapterNum}>Kapitel 10</div>
          <h2 style={S.chapterTitle}>Anhang und Referenzen</h2>
        </div>

        <h3 id="k10-1" style={S.h3}>10.1 Statusübersicht (Farbreferenz)</h3>
        <h4 style={S.h4}>Genehmigungsstatus</h4>
        <Table
          headers={['Badge', 'Status', 'Bedeutung']}
          rows={[
            ['<span style="background:#f3f2f1;color:#605e5c;padding:2px 8px;border-radius:12px;font-size:11px">● Kein Flow</span>', 'Kein Flow', 'Kein Genehmigungsflow angelegt'],
            ['<span style="background:#fff4ce;color:#d83b01;padding:2px 8px;border-radius:12px;font-size:11px">● Offen</span>', 'Offen', 'Mindestens ein Flow wartet auf Genehmigung'],
            ['<span style="background:#eff6fc;color:#0078d4;padding:2px 8px;border-radius:12px;font-size:11px">● Teilweise</span>', 'Teilweise genehmigt', 'Einige Flows genehmigt, andere noch offen'],
            ['<span style="background:#dff6dd;color:#107c10;padding:2px 8px;border-radius:12px;font-size:11px">● Genehmigt</span>', 'Genehmigt', 'Alle Flows vollständig genehmigt'],
            ['<span style="background:#fde7e9;color:#a4262c;padding:2px 8px;border-radius:12px;font-size:11px">● Abgelehnt</span>', 'Abgelehnt', 'Mindestens ein Flow abgelehnt'],
          ]}
        />
        <h4 style={S.h4}>Bestellstatus</h4>
        <Table
          headers={['Badge', 'Status', 'Bedeutung']}
          rows={[
            ['<span style="background:#fff4ce;color:#d83b01;padding:2px 8px;border-radius:12px;font-size:11px">● Eingereicht</span>', 'Eingereicht', 'Eingereicht, noch nicht bestellt'],
            ['<span style="background:#eff6fc;color:#0078d4;padding:2px 8px;border-radius:12px;font-size:11px">● Bestellt</span>', 'Bestellt', 'Beim Lieferanten aufgegeben'],
            ['<span style="background:#e8f5e9;color:#5db85d;padding:2px 8px;border-radius:12px;font-size:11px">● Teilweise geliefert</span>', 'Teilweise geliefert', 'Ein Teil der Ware wurde empfangen (lt. BC)'],
            ['<span style="background:#dff6dd;color:#107c10;padding:2px 8px;border-radius:12px;font-size:11px">● Geliefert</span>', 'Geliefert', 'Ware vollständig empfangen'],
            ['<span style="background:#fde7e9;color:#a4262c;padding:2px 8px;border-radius:12px;font-size:11px">● Storniert</span>', 'Storniert', 'Bestellung storniert'],
          ]}
        />

        <h3 id="k10-2" style={S.h3}>10.2 Häufig gestellte Fragen (FAQ)</h3>
        <h4 style={S.h4}>Warum sehe ich bestimmte Bestellungen nicht?</h4>
        <ul style={S.ul}>
          <li>Sie benötigen mind. Rolle <em>DGS-Viewer</em>.</li>
          <li>Bestellung ist <strong>vertraulich</strong> und Sie sind nicht eingebunden.</li>
          <li>Bestellung wurde archiviert – wechseln Sie zum Archiv.</li>
        </ul>
        <h4 style={S.h4}>Warum kann ich keine Bestellung bearbeiten?</h4>
        <ul style={S.ul}>
          <li>Mind. <em>DGS-Erstellen und bearbeiten</em> erforderlich.</li>
          <li>Nur der Ersteller darf bearbeiten (außer Admin/Einkauf).</li>
          <li>Bestellung ist archiviert.</li>
        </ul>
        <h4 style={S.h4}>Was passiert bei Änderung der Freigabe-Matrix?</h4>
        <p>Genehmiger werden dynamisch geladen – Änderungen gelten sofort, keine manuellen Flow-Updates nötig.</p>
        <h4 style={S.h4}>Kann ich eine Freigabe rückgängig machen?</h4>
        <p>Nein – Freigaben sind endgültig. Admin/Einkauf kann den Flow löschen und neu anlegen.</p>
        <h4 style={S.h4}>Wozu dient „Zu bearbeiten"?</h4>
        <p>Zeigt Bestellungen mit offenem Freigabeschritt oder unbeantworteter @-Erwähnung.</p>

        <h3 id="k10-3" style={S.h3}>10.3 Rollenmatrix – Funktionsübersicht</h3>
        <Table
          headers={['Funktion', 'Viewer', 'Erstellen', 'Freigeber', 'GF', 'Einkauf', 'Admin']}
          rows={[
            ['Bestellungen ansehen', '✓', '✓', '✓', '✓', '✓', '✓'],
            ['Eigene Bestellung erstellen', '–', '✓', '✓', '✓', '✓', '✓'],
            ['Eigene Bestellung bearbeiten', '–', '✓', '✓', '✓', '✓', '✓'],
            ['Alle Bestellungen bearbeiten', '–', '–', '–', '–', '✓', '✓'],
            ['Bestellung archivieren', '–', '–', '–', '–', '✓', '✓'],
            ['Bestellstatus ändern', '–', '–', '–', '–', '✓', '✓'],
            ['Vertraulich setzen', '–', 'Eigene', '–', '–', '✓', '✓'],
            ['Abteilungsflows freigeben', '–', '–', '✓', '–', '✓', '✓'],
            ['GF-Flows freigeben', '–', '–', '–', '✓', '–', '✓'],
            ['Flows anlegen / löschen', '–', '–', '–', '–', '✓', '✓'],
            ['Kommentare schreiben', '✓', '✓', '✓', '✓', '✓', '✓'],
            ['Dokumente hochladen', '–', '✓', '✓', '✓', '✓', '✓'],
            ['Eigene Dokumente löschen', '–', '✓', '✓', '✓', '✓', '✓'],
            ['Alle Dokumente löschen', '–', '–', '–', '–', '✓', '✓'],
            ['Stammdaten anlegen/bearbeiten', '–', '–', '–', '–', '✓', '✓'],
            ['Dokumenttypen verwalten', '–', '–', '–', '–', '✓', '✓'],
            ['Berechtigungen verwalten', '–', '–', '–', '–', '–', '✓'],
            ['Freigabe-Matrix bearbeiten', '–', '–', '–', '–', '–', '✓'],
            ['Datensynchronisation', '–', '–', '–', '–', '✓', '✓'],
            ['E-Mail / SMTP konfigurieren', '–', '–', '–', '–', '–', '✓'],
            ['Benachrichtigungs-Templates', '–', '–', '–', '–', '–', '✓'],
          ]}
        />

        <BackToToc onClick={() => scrollTo('toc')} />
      </>}

      <div style={{ marginTop: 40, color: '#a19f9d', fontSize: 12, textAlign: 'center' }}>
        Bestellwesen – Bedienungshandbuch · Bögra Technologie GmbH · Version 1.4 · Juli 2026
      </div>
    </>
  );
};
